import styled from '@emotion/styled';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import {
  DSButton,
  DSContentBlock,
  DSContextMenu,
  DSContextMenuButton,
} from '@hundred5/design-system';
import { Form, Formik } from 'formik';
import { usePostHog } from 'posthog-js/react';
import React, { useEffect, useRef, useState } from 'react';
import { object, string } from 'yup';

import { getAmplitudeClient } from '@/features/amplitude/utils/amplitude';
import { FormikInputField, Icon, PromptIfDirty, TId } from '@/features/common';
import CardButton from '@/features/common/components/card-button';
import { DeleteConfirmationContextMenu } from '@/features/common/components/delete-confirmation-context-menu';
import { useAnalytics } from '@/features/common/hooks/use-analytics';
import { useClickOutside } from '@/features/common/hooks/use-click-outside';
import { useNotifications } from '@/features/notifications';
import { useJobOpeningPermission } from '@/features/permissions';
import { useGenericPricingUpsellModalContext } from '@/features/upsell';
import { usePlanLimits } from '@/hooks/planLimits';
import { useHistory } from '@/hooks/router';
import { useUpdateInterviewMutation } from '@/queries/interviews/mutations';
import { useGetInterview } from '@/queries/interviews/queries';
import { Interview } from '@/queries/types';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { InterviewAICreator } from '../interview-ai-creator';
import { InterviewCreator } from '../interview-creator';

interface InterviewSettingsProps {
  interviewId: TId;

  workspaceId: number;
  openingId: number;
  stageId: number;
}

interface Settings {
  name: string;
  schedulingLink: string;
}

const validationSchema = object().shape({
  name: string()
    .max(50, "Name can't be longer than 50 characters!")
    .required("This can't be blank!"),
  schedulingLink: string()
    .url('This has to be a valid link!')
    .nullable()
    .max(256, "Scheduling link can't be longer than 256 characters!"),
});

export function InterviewSettings({
  interviewId,
  workspaceId,
  openingId,
  stageId,
}: InterviewSettingsProps) {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { addNotification } = useNotifications();
  const canUpdate = useJobOpeningPermission()('test', 'update');
  const amplitude = getAmplitudeClient();
  const posthog = usePostHog();

  const interviewQuery = useGetInterview(workspaceId, openingId, stageId);
  const updateInterviewMutation = useUpdateInterviewMutation({
    meta: {
      invalidates: 'all',
    },
  });

  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const contextMenuRef = useRef<null | HTMLDivElement>(null);
  useClickOutside(contextMenuRef, () => setIsContextMenuOpen(false));
  const { trackEvent } = useAnalytics('interview');
  const canAccessInterviewCreator = !!usePlanLimits()('interview_creator');
  const { openPricingUpsell } = useGenericPricingUpsellModalContext();

  useEffect(() => {
    amplitude?.logEvent(`page view/open interview settings`, {
      'workspace id': workspaceId,
      'job opening id': openingId,
      'interview id': interviewId,
    });
    posthog?.capture(`hire_admin_app:page view/open interview settings`, {
      'workspace id': workspaceId,
      'job opening id': openingId,
      'interview id': interviewId,
    });
  }, [amplitude, interviewId, openingId, workspaceId, posthog]);

  const handleSubmit = async (values: Partial<Interview>) => {
    // if (!interview) return; is this even needed?

    const schedulingLinkChanged =
      values.schedulingLink !== interviewQuery.data?.schedulingLink;

    await updateInterviewMutation.mutateAsync({
      workspaceId,
      openingId,
      stageId,
      data: {
        ...interviewQuery.data!,
        ...values,
      },
    });

    if (schedulingLinkChanged) {
      amplitude?.logEvent('interview/save scheduling link', {
        'workspace id': workspaceId,
        'job opening id': openingId,
        'interview id': interviewId,
      });
      posthog?.capture('hire_admin_app:interview/save scheduling link', {
        'workspace id': workspaceId,
        'job opening id': openingId,
        'interview id': interviewId,
      });
    }

    addNotification({ type: 'saved' });
  };

  const handleCreateInterviewKit = () => {
    trackEvent('add_questions_clicked', {
      workspace_id: workspaceId,
      job_opening_id: openingId,
      interview_id: interviewId,
    });

    if (!canAccessInterviewCreator) {
      openPricingUpsell('generic');
    } else {
      setIsContextMenuOpen(false);
      history.push(
        `/admin/${workspaceId}/openings/${openingId}/${stageId}/interview/${interviewId}/settings/create`
      );
    }
  };

  const handleOpenInterviewKit = () => {
    setIsContextMenuOpen(false);
    history.push(
      `/admin/${workspaceId}/openings/${openingId}/${stageId}/interview/${interviewId}/settings/create`
    );
  };

  const handleCreateInterviewKitWithAi = () => {
    setIsContextMenuOpen(false);
    history.push(
      `/admin/${workspaceId}/openings/${openingId}/${stageId}/interview/${interviewId}/settings/create-with-ai`
    );
  };

  const handleDeleteInterviewKit = () => {
    setIsContextMenuOpen(false);
    const interview = interviewQuery.data!;
    delete interview.kit;

    updateInterviewMutation.mutate({
      workspaceId,
      openingId,
      stageId,
      data: interview,
    });
  };

  return (
    <InterviewSettingsContainer>
      <Formik<Settings>
        enableReinitialize
        initialValues={{
          ...interviewQuery.data,
          name: interviewQuery.data?.name ?? '',
          schedulingLink: interviewQuery.data?.schedulingLink ?? '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ dirty, isSubmitting }) => (
          <Form>
            <PromptIfDirty />

            <DSContentBlock>
              <DSContentBlock.Title>General</DSContentBlock.Title>

              <DSContentBlock.Content>
                <ContentWrapper>
                  <TextInputField
                    name="name"
                    label="Interview name"
                    required
                    autoFocus
                  />
                  <div>
                    <TextInputField
                      name="schedulingLink"
                      label="Scheduling link"
                      placeholder="https://calendly.com/johndoe-1234/45min"
                    />
                    <InfoLink
                      href="https://support.hire.toggl.com/en/articles/9564596-how-to-use-a-scheduling-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      How can I add a scheduling link?
                    </InfoLink>
                  </div>
                </ContentWrapper>
              </DSContentBlock.Content>

              <DSContentBlock.Footer>
                <DSButton
                  type="submit"
                  disabled={!canUpdate || !dirty || isSubmitting}
                  data-rh={!canUpdate ? 'Only admins can save changes' : null}
                >
                  {isSubmitting ? 'Saving...' : 'Save changes'}
                </DSButton>
              </DSContentBlock.Footer>
            </DSContentBlock>
          </Form>
        )}
      </Formik>

      <DSContentBlock>
        <div className=" pt-9 pb-4 px-9 border-b border-primary">
          <div className="flex items-center mb-4 gap-2">
            <h2 className="text-h4">Interview questions and scorecard</h2>
            <Tag>NEW</Tag>
          </div>
          <p className="text-p1">
            Create interview questions that appear on candidate profiles for
            easy evaluation and scoring.
            <br />
            Learn more about interviews{' '}
            <a
              className="underline text-primary"
              href="https://support.hire.toggl.com/en/articles/11894614-interview-scorecards"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            .
          </p>
        </div>
        <DSContentBlock.Content style={{ padding: '36px' }}>
          {interviewQuery.data?.kit ? (
            <InterviewKitContainer>
              <span>{interviewQuery.data.name}</span>

              <DSButton variant="tertiary" onClick={handleOpenInterviewKit}>
                Open
              </DSButton>

              <ContextMenuContainer ref={contextMenuRef}>
                <DSContextMenuButton
                  onClick={() => setIsContextMenuOpen(!isContextMenuOpen)}
                />
                <DSContextMenu
                  open={isContextMenuOpen}
                  position={{ top: '28px', right: '0' }}
                >
                  <DSContextMenu.Item
                    label="Delete"
                    icon={<Icon icon={regular('trash')} />}
                    dangerous
                    onClick={() => {
                      setIsContextMenuOpen(false);
                      setIsDeleteConfirmOpen(true);
                    }}
                  ></DSContextMenu.Item>
                </DSContextMenu>
                <DeleteConfirmationContextMenu
                  open={isDeleteConfirmOpen}
                  title="Delete interview questions"
                  message="Are you sure? You will not be able to restore these questions later."
                  deleteButtonText="Delete"
                  onClick={() => {
                    setIsDeleteConfirmOpen(false);
                    handleDeleteInterviewKit();
                  }}
                  onClose={() => setIsDeleteConfirmOpen(false)}
                  position={{ top: '-170px', right: '0' }}
                />
              </ContextMenuContainer>
            </InterviewKitContainer>
          ) : (
            <div className="flex gap-6">
              <CardButton
                title="Build custom interview"
                description="Write or paste your own questions"
                onClick={handleCreateInterviewKit}
              />
              <CardButton
                title="Generate with AI"
                description="Generate your interview questions using AI"
                tagText="NEW"
                onClick={handleCreateInterviewKitWithAi}
              />
            </div>
          )}
        </DSContentBlock.Content>
      </DSContentBlock>

      <Switch>
        <Route
          path={`${url}/create`}
          render={({ match }) => (
            <InterviewCreator
              workspaceId={workspaceId}
              openingId={openingId}
              stageId={stageId}
              open={!!match}
              onClose={() => history.replace(url)}
            />
          )}
        />

        <Route
          path={`${url}/create-with-ai`}
          render={({ match }) => (
            <InterviewAICreator
              workspaceId={workspaceId}
              jobOpeningId={openingId}
              stageId={stageId}
              onClose={() => history.replace(url)}
              interview={interviewQuery.data}
            />
          )}
        />
      </Switch>
    </InterviewSettingsContainer>
  );
}

const InterviewSettingsContainer = styled.div`
  display: grid;
  gap: 10px;
`;

const ContentWrapper = styled.div`
  display: grid;
  gap: 16px;
`;

const TextInputField = styled(FormikInputField)`
  max-width: 364px;
`;

const InfoLink = styled.a`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.typography.colorSecondary};
  text-decoration: underline;
`;

const ContextMenuContainer = styled.div`
  position: relative;
`;

const InterviewKitContainer = styled.div`
  display: grid;
  grid-template-columns: auto min-content min-content;
  gap: 8px;
  align-items: center;
  padding: 24px 20px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.purple[20]};

  span {
    font-size: 16px;
    font-weight: 500;
    color: ${({ theme }) => theme.typography.colorPrimary};
  }
`;

const Tag = styled.span`
  align-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.orange[20]};
  border-radius: 8px;
  display: flex;
  font-size: 10px;
  height: 16px;
  justify-content: center;
  padding: 0px 8px;
`;
